import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { publicLink } from "@/lib/landerDomain";
import { IdentificationCard, Copy } from "@phosphor-icons/react";
import api from "@/lib/api";
import { ChangeHistoryTable } from "@/components/BuyerProfileChanges";
import { SendTestLeadButton } from "@/components/SendTestLeadButton";
import { DeliveryCheckBadge } from "@/components/GlobalBuyersPanel";
import { BuyerFunnelPicks } from "@/components/buyer/BuyerFunnelPicks";
import { TestimonialsManager } from "@/components/testimonials/TestimonialsManager";
import { BuyerThankYouDesigns } from "@/components/thankyou/BuyerThankYouDesigns";

const PHONE_NOTE = {
  none: "Phone numbers are currently hidden on the funnels. Your number is saved and will show once phone display is turned on.",
  thank_you: "Your phone number shows on your thank-you pages only, not on the landing pages.",
  landers: "Your phone number shows on the landing pages only, not on your thank-you pages.",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BTN = "border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1 disabled:opacity-40";
const INPUT = "w-full border border-black px-3 py-2 text-sm mt-1";

const CONSENT_TOASTS = {
  pending_approval: "Consent text sent for admin approval",
  reverted_to_standard: "Standard consent wording restored",
  withdrawn: "Pending consent change withdrawn",
};

// Name, phone and logo are read-only here; buyers ask for changes in "Your company details".
function IdentityView({ profile }) {
  return (
    <div className="flex flex-col gap-2 border-2 border-dashed border-zinc-400 p-3 text-xs" data-testid={`buyer-profile-identity-${profile.id}`}>
      <div><span className="label-mono">Company name</span> <b data-testid={`buyer-profile-firm-${profile.id}`}>{profile.firm_name || "—"}</b></div>
      <div><span className="label-mono">Phone</span> <span data-testid={`buyer-profile-phone-${profile.id}`}>{profile.phone || "—"}</span></div>
      {PHONE_NOTE[profile.phone_display] && (
        <div className="text-[10.5px] text-zinc-600" data-testid={`buyer-profile-phone-hidden-${profile.id}`}>{PHONE_NOTE[profile.phone_display]}</div>
      )}
      <div className="flex items-center gap-2">
        <span className="label-mono">Logo</span>
        <div className="flex h-10 w-24 items-center justify-center overflow-hidden border border-black bg-zinc-900">
          {profile.logo_url
            ? <img src={`${BACKEND}${profile.logo_url}`} alt="Your logo" className="max-h-9 max-w-[88px] object-contain" data-testid={`buyer-profile-logo-preview-${profile.id}`} />
            : <span className="text-[9px] uppercase tracking-widest text-zinc-400">No logo</span>}
        </div>
      </div>
      <div className="text-[10.5px] text-zinc-600">To change your name or phone, use <b>Request a change</b> under Your company details. For a new logo, use <b>Request a new logo</b> there.</div>
    </div>
  );
}

function OwnPages({ profile }) {
  if (!profile.own_pages?.length) return null;
  const copy = (path) => {
    if (navigator.clipboard) navigator.clipboard.writeText(publicLink(path));
    toast.success("Link copied");
  };
  return (
    <div className="border-t border-zinc-200 pt-3" data-testid={`buyer-own-pages-${profile.id}`}>
      <div className="label-mono mb-1">Your own pages</div>
      <div className="flex flex-col gap-1.5">
        {profile.own_pages.map((p) => (
          <div key={p.slug} className="flex items-center gap-2 border-2 border-black px-2 py-1.5 text-[11px]" data-testid={`buyer-own-page-${p.slug}`}>
            <span className="min-w-0 flex-1 truncate" title={p.name}>{p.name}</span>
            <a href={publicLink(p.path)} target="_blank" rel="noreferrer" className="font-mono text-[10px] underline" data-testid={`buyer-own-page-open-${p.slug}`}>{p.path}</a>
            <button type="button" onClick={() => copy(p.path)} aria-label={`Copy the ${p.name} link`}
                    className="border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors duration-100"
                    data-testid={`buyer-own-page-copy-${p.slug}`}>
              <Copy size={10} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeliveryCheck({ profile, onTestSent }) {
  if (!profile.is_test && !profile.last_test) return null;
  return (
    <div data-testid={`buyer-profile-test-lead-wrap-${profile.id}`}>
      <div className="label-mono mb-1 flex items-center gap-2 flex-wrap">
        Last delivery check
        <DeliveryCheckBadge t={profile.last_test} testId={`buyer-profile-check-badge-${profile.id}`} />
      </div>
      {profile.is_test && (
        <>
          <div className="label-mono mb-1">Test profile — send a TEST lead to check delivery</div>
          <SendTestLeadButton url={`/buyer/funnel-profiles/${profile.id}/test-lead`} testId={`buyer-profile-test-lead-${profile.id}`}
                              onSent={onTestSent} funnels={profile.funnel_picks} states={profile.states} />
        </>
      )}
    </div>
  );
}

function ConsentField({ profile, value, onChange }) {
  return (
    <div>
      <label className="label-mono" htmlFor={`consent-${profile.id}`}>Consent text</label>
      {profile.pending_tcpa && (
        <div className="border-2 border-amber-600 bg-amber-50 p-2 text-[11px] mt-1" data-testid={`buyer-profile-consent-pending-${profile.id}`}>
          Your new consent text is waiting for admin approval (sent {new Date(profile.pending_tcpa.submitted_at).toLocaleString()}).
          Visitors keep seeing the current text until it&apos;s approved.
        </div>
      )}
      <textarea id={`consent-${profile.id}`} rows={5} value={value} onChange={(e) => onChange(e.target.value)}
                placeholder="Leave blank to use our standard consent wording with your company name."
                className={`${INPUT} text-xs leading-relaxed`} data-testid={`buyer-profile-consent-${profile.id}`} />
      <details className="mt-1">
        <summary className="text-[10px] uppercase tracking-widest cursor-pointer text-zinc-600">What visitors see right now</summary>
        <div className="border border-zinc-300 bg-zinc-50 p-2 text-[11px] leading-relaxed mt-1" data-testid={`buyer-profile-consent-live-${profile.id}`}>
          {profile.effective_tcpa}
        </div>
      </details>
    </div>
  );
}

function ProfileHistory({ profileId }) {
  const [changes, setChanges] = useState(null);
  const onToggle = (e) => {
    if (!e.currentTarget.open || changes) return;
    api.get(`/buyer/funnel-profiles/${profileId}/history`)
      .then((r) => setChanges(r.data?.changes || []))
      .catch(() => setChanges([]));
  };
  return (
    <details className="mt-3" onToggle={onToggle} data-testid={`buyer-profile-history-${profileId}`}>
      <summary className="text-[10px] uppercase tracking-widest cursor-pointer">Change history</summary>
      <div className="mt-2">
        {changes ? <ChangeHistoryTable changes={changes} testId={`buyer-profile-history-table-${profileId}`} showFirm={false} />
          : <div className="label-mono">Loading…</div>}
      </div>
    </details>
  );
}

function initialForm(p) {
  return {
    tcpa_language: p.pending_tcpa?.text ?? p.tcpa_language ?? "",
  };
}

function ProfileEditor({ profile, onUpdated, onTestSent }) {
  const initial = useMemo(() => initialForm(profile), [profile]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = Object.keys(initial).filter((k) => form[k] !== initial[k]);

  const save = async () => {
    const body = Object.fromEntries(dirty.map((k) => [k, form[k]]));
    setSaving(true);
    try {
      const r = await api.patch(`/buyer/funnel-profiles/${profile.id}`, body);
      onUpdated(r.data.profile);
      if (r.data.consent && CONSENT_TOASTS[r.data.consent]) toast.success(CONSENT_TOASTS[r.data.consent]);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save your changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 border-black bg-white p-4" data-testid={`buyer-profile-${profile.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-3">
          <IdentityView profile={profile} />
        </div>
        <div className="flex flex-col gap-3">
          <ConsentField profile={profile} value={form.tcpa_language} onChange={set("tcpa_language")} />
          <BuyerFunnelPicks key={`${profile.id}-${(profile.funnel_picks || []).join(",")}`} profile={profile} onUpdated={onUpdated} />
          <OwnPages profile={profile} />
          <DeliveryCheck profile={profile} onTestSent={onTestSent} />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button type="button" onClick={save} disabled={!dirty.length || saving}
                data-testid={`buyer-profile-save-${profile.id}`}
                className="border-2 border-black bg-black text-white px-4 py-2 text-[11px] uppercase tracking-widest disabled:opacity-40">
          {saving ? "Saving…" : "Save consent text"}
        </button>
        <span className="text-[10.5px] text-zinc-600">Consent text needs admin approval.</span>
      </div>
      <div className="mt-5 border-t border-zinc-300 pt-4" data-testid={`buyer-testimonials-${profile.id}`}>
        <div className="font-chivo font-black uppercase tracking-tight text-sm">Client testimonials</div>
        <p className="text-[10.5px] text-zinc-600 mb-2">Add real reviews from your clients. Each one shows on your pages after an admin approves it.</p>
        <TestimonialsManager pageId={profile.id} admin={false} />
      </div>
      <div className="mt-5 border-t border-zinc-300 pt-4" data-testid={`buyer-ty-test-${profile.id}`}>
        <BuyerThankYouDesigns profile={profile} />
      </div>
      <ProfileHistory profileId={profile.id} />
    </div>
  );
}

export const BuyerFunnelProfiles = () => {
  const [profiles, setProfiles] = useState(null);
  const load = useCallback(() => {
    api.get("/buyer/funnel-profiles")
      .then((r) => setProfiles(r.data?.profiles || []))
      .catch(() => setProfiles([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const onUpdated = (p) => setProfiles((list) => list.map((x) => (x.id === p.id ? p : x)));

  return (
    <div className="mt-8 brut-card p-5" data-testid="buyer-funnel-profiles">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <IdentificationCard size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">Your company on our funnels</div>
      </div>
      <p className="text-[11px] text-zinc-600 leading-relaxed mb-4">
        These details appear on every shared funnel you get leads from, whenever a visitor is sent to you.
      </p>
      {profiles === null && <div className="label-mono">Loading…</div>}
      {profiles?.length === 0 && (
        <div className="label-mono text-zinc-500" data-testid="buyer-funnel-profiles-empty">
          No funnel profile is linked to your account yet. Contact your account manager to set one up.
        </div>
      )}
      <div className="flex flex-col gap-4">
        {(profiles || []).map((p) => <ProfileEditor key={p.id} profile={p} onUpdated={onUpdated} onTestSent={load} />)}
      </div>
    </div>
  );
};
